import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { repeatText } from "../lib/repeat";

type SepMode = "newline" | "space" | "comma" | "custom";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      {label}
    </label>
  );
}

export function TextRepeatTool() {
  const uid = useId();
  const [input, setInput] = useState("Toolverse");
  const [count, setCount] = useState(3);
  const [sepMode, setSepMode] = useState<SepMode>("newline");
  const [customSep, setCustomSep] = useState(" | ");
  const [addNumbering, setAddNumbering] = useState(false);
  const [trimResult, setTrimResult] = useState(false);
  const [copied, setCopied] = useState(false);

  const separator =
    sepMode === "newline"
      ? "\n"
      : sepMode === "space"
        ? " "
        : sepMode === "comma"
          ? ", "
          : customSep;

  const output = useMemo(
    () => repeatText(input, count, { separator, addNumbering, trimResult }),
    [input, count, separator, addNumbering, trimResult],
  );

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.tr_text_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.tr_placeholder()}
            className="min-h-[6rem] w-full resize-y rounded-lg border border-border bg-background p-4 text-sm"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-count`}>{m.tr_times_label()}</Label>
            <Input
              id={`${uid}-count`}
              type="number"
              min={1}
              max={10000}
              value={count}
              onChange={(e) => setCount(Number(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-sep`}>{m.tr_separator()}</Label>
            <select
              id={`${uid}-sep`}
              className={selectClass}
              value={sepMode}
              onChange={(e) => setSepMode(e.target.value as SepMode)}
            >
              <option value="newline">{m.tr_sep_newline()}</option>
              <option value="space">{m.tr_sep_space()}</option>
              <option value="comma">{m.tr_sep_comma()}</option>
              <option value="custom">{m.tr_sep_custom()}</option>
            </select>
          </div>
          {sepMode === "custom" && (
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-custom`}>{m.tr_custom_label()}</Label>
              <Input
                id={`${uid}-custom`}
                value={customSep}
                onChange={(e) => setCustomSep(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <Toggle
            checked={addNumbering}
            onChange={setAddNumbering}
            label={m.tr_numbering()}
          />
          <Toggle
            checked={trimResult}
            onChange={setTrimResult}
            label={m.tr_trim()}
          />
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-out`}>{m.tr_output_label()}</Label>
          <div className="flex items-center gap-3">
            <span
              data-testid="tr-chars"
              className="text-xs text-muted-foreground"
            >
              {m.tr_chars({ n: String(output.length) })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              disabled={!output}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.tr_copied() : m.tr_copy()}
            </Button>
          </div>
        </div>
        <textarea
          id={`${uid}-out`}
          readOnly
          data-testid="tr-output"
          value={output}
          spellCheck={false}
          className="min-h-[12rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
      </div>
    </div>
  );
}
